// src/pages/admin/AdminLeave.jsx
import React, { useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { deductLeaveBalance } from "../../hooks/useFirestore";
import { useLeave } from "../../context/LeaveContext";
import { ADMIN_NAV } from "../../utils/navigation";
import Layout from "../../components/common/Layout";

// Helper: human-readable status label
function statusLabel(status) {
  switch (status) {
    case "pending_lead":   return "Pending Lead";
    case "pending_admin":  return "Pending Admin";
    case "approved":       return "Approved";
    case "rejected":       return "Rejected";
    default:               return status;
  }
}

// Helper: pill colour
function statusColor(status) {
  if (status === "approved") return "green";
  if (status === "rejected") return "red";
  return "amber";
}

// ============================================================================
// >>> NEW FUNCTION ADDED: formatAppliedOn (Lines 28–45) <<<
// ============================================================================
// Helper: format submission timestamp into "DD MMM YYYY, hh:mm A" (e.g. 25 Aug 2026, 01:30 PM)
function formatAppliedOn(ts) {
  if (!ts) return "—";
  const date = typeof ts.toDate === "function" ? ts.toDate() : new Date(ts);
  if (isNaN(date.getTime())) return "—";

  const day = date.getDate().toString().padStart(2, "0");
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, "0");
  const ampm = hours >= 12 ? "PM" : "AM";
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = hours.toString().padStart(2, "0");

  return `${day} ${month} ${year}, ${strHours}:${minutes} ${ampm}`;
}

// Requests that admin can act on
const ACTIONABLE = ["pending_admin", "pending_lead"];

export default function AdminLeave() {
  const { userProfile } = useAuth();
  const { allRequests, allRequestsLoading, adminReviewLeaveRequest } = useLeave();

  // Default: show ALL requests
  const [filterStatus, setFilterStatus] = useState("all");

  // processing[id] = false | "loading" | "done"
  const [processing, setProcessing] = useState({});
  const [actionMsg, setActionMsg] = useState("");
  const [actionError, setActionError] = useState("");

  // ── Filter logic ──
  const filtered = allRequests.filter((r) => {
    if (filterStatus === "all") return true;
    if (filterStatus === "pending")
      return r.status === "pending_lead" || r.status === "pending_admin";
    return r.status === filterStatus;
  });

  // ── Pending count (pending_lead + pending_admin) ──
  const pendingCount = allRequests.filter((r) =>
    ACTIONABLE.includes(r.status),
  ).length;

  async function handleLeaveAction(request, action) {
    if (!userProfile?.uid) {
      setActionError("User profile not ready. Please wait a moment and try again.");
      return;
    }
    if (processing[request.id]) return; // Prevent duplicate clicks

    setActionMsg("");
    setActionError("");
    setProcessing((p) => ({ ...p, [request.id]: "loading" }));

    try {
      await adminReviewLeaveRequest(request.id, action, userProfile.uid);

      if (action === "approve") {
        await deductLeaveBalance(request.userId, request.leaveType, request.totalDays);
      }

      setProcessing((p) => ({ ...p, [request.id]: "done" }));
      setActionMsg(
        `${request.displayName}'s leave has been ${action === "approve" ? "approved ✅" : "rejected ❌"}.`,
      );
    } catch (err) {
      console.error("[AdminLeave] action error:", err);
      setActionError(`Failed to ${action} request: ${err.message || "Unknown error"}`);
      setProcessing((p) => ({ ...p, [request.id]: false }));
    }
  }

  return (
    <Layout navItems={ADMIN_NAV} panelLabel="Admin Panel">
      <div className="page-header">
        <h1 className="page-title">Leave Requests</h1>
        <span className="status-pill status-pill--amber">
          {pendingCount} pending
        </span>
      </div>

      {/* Action feedback */}
      {actionMsg && (
        <div
          style={{
            background: "var(--green-bg)",
            color: "var(--green-text)",
            border: "1px solid var(--green)",
            borderRadius: "var(--radius)",
            padding: "0.65rem 1rem",
            fontSize: "0.85rem",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{actionMsg}</span>
          <button
            onClick={() => setActionMsg("")}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", color: "var(--green-text)" }}
          >
            ✕
          </button>
        </div>
      )}
      {actionError && (
        <div
          style={{
            background: "var(--red-bg)",
            color: "var(--red-text)",
            border: "1px solid var(--red)",
            borderRadius: "var(--radius)",
            padding: "0.65rem 1rem",
            fontSize: "0.85rem",
            marginBottom: "1rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>❌ {actionError}</span>
          <button
            onClick={() => setActionError("")}
            style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1rem", color: "var(--red-text)" }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Filters — default is "all" */}
      <div className="table-filters">
        <select
          className="filter-select"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Requests</option>
          <option value="pending">Pending</option>
          <option value="pending_admin">Pending Admin</option>
          <option value="pending_lead">Pending Lead</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {allRequestsLoading ? (
        <div className="card-loading">Loading leave requests…</div>
      ) : filtered.length === 0 ? (
        <div className="card-empty">
          No {filterStatus === "all" ? "" : filterStatus} leave requests.
        </div>
      ) : (
        <div className="data-table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee</th>
                <th>Type</th>
                {/* >>> NEW HEADER ADDED: (Line 192) <<< */}
                <th>Applied On</th>
                <th>From</th>
                <th>To</th>
                <th>Days</th>
                <th>Reason</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => {
                const isLoading = processing[r.id] === "loading";
                const isDone = processing[r.id] === "done";
                const canAct = ACTIONABLE.includes(r.status) && !isDone;

                return (
                  <tr key={r.id}>
                    <td>
                      <div className="table-user-cell">
                        <div className="table-avatar">
                          {r.displayName?.slice(0, 2).toUpperCase()}
                        </div>
                        <div>
                          <div>{r.displayName}</div>
                          <div style={{ fontSize: "0.72rem", color: "var(--text-muted)" }}>
                            {r.department}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td style={{ textTransform: "capitalize" }}>
                      {r.isHalfDay
                        ? `Half Day (${r.halfDaySession === "second_half" ? "Second Half 🌙" : "First Half ☀️"})`
                        : `${r.leaveType} Leave`}
                    </td>
                    {/* >>> NEW TABLE CELL ADDED: (Lines 227–229) <<< */}
                    <td style={{ whiteSpace: "nowrap", color: "var(--text-secondary)", fontSize: "0.8rem" }}>
                      {formatAppliedOn(r.createdAt)}
                    </td>
                    <td>{r.fromDate}</td>
                    <td>{r.toDate}</td>
                    <td>{r.totalDays}</td>
                    <td
                      style={{
                        maxWidth: "250px",
                        wordBreak: "break-word",
                      }}
                    >
                      {r.reason}
                      {r.medicalCertificateData && (
                        <div style={{ marginTop: "0.3rem" }}>
                          <a
                            href={r.medicalCertificateData}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ fontSize: "0.78rem", color: "#2563eb", textDecoration: "underline", fontWeight: 500 }}
                          >
                            📄 Med Cert ({r.medicalCertificateName || "View"})
                          </a>
                        </div>
                      )}
                    </td>
                    <td>
                      <span className={`status-pill status-pill--${statusColor(r.status)}`}>
                        {statusLabel(r.status)}
                      </span>
                    </td>
                    <td>
                      {canAct ? (
                        <div className="action-btns">
                          <button
                            className="btn-success-sm"
                            onClick={() => handleLeaveAction(r, "approve")}
                            disabled={isLoading || !userProfile?.uid}
                            style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
                          >
                            {isLoading ? "…" : "✓ Approve"}
                          </button>
                          <button
                            className="btn-danger-sm"
                            onClick={() => handleLeaveAction(r, "reject")}
                            disabled={isLoading || !userProfile?.uid}
                            style={{ cursor: isLoading ? "not-allowed" : "pointer" }}
                          >
                            {isLoading ? "…" : "✗ Reject"}
                          </button>
                        </div>
                      ) : (
                        <span style={{ fontSize: "0.8rem", color: "var(--text-muted)" }}>
                          {r.status === "approved" || r.status === "rejected"
                            ? "Reviewed"
                            : "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Layout>
  );
}